import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { useDispatch } from 'react-redux';
import { persistReducer } from 'redux-persist';

import { localStorageAdapter } from './localStorageAdapter';
import { audioInfoSlice } from './slices/audioInfoSlice';
import { authSerialSlice } from './slices/authSerialSlice';
import { browserAudioSettingSlice } from './slices/browserAudioSettingSlice';
import { browserHistorySlice } from './slices/browserHistorySlice';
import { browserLanguageSlice } from './slices/browserLanguageSlice';
import { browserTranslationDisplaySlice } from './slices/browserTranslationDisplaySlice';
import { browserUILanguageSlice } from './slices/browserUILanguageSlice';
import { commonErrorDialogSlice } from './slices/commonErrorDialogSlice';
import { glossaryEntriesSlice } from './slices/glossaryEntriesSlice';
import { googleTtsVoiceListSlice } from './slices/googleTtsVoiceListSlice';
import { guestDisplaySlice } from './slices/guestDisplaySlice';
import { languageListSlice } from './slices/languageListSlice';
import { loginUserInfoSlice } from './slices/loginUserInfoSlice';
import { shareViewEndInfoSlice } from './slices/shareViewEndInfoSlice';
import { shareViewInfoSlice } from './slices/shareViewInfoSlice';
import { toastInfoSlice } from './slices/toastInfoSlice';
import { translationDisplaySlice } from './slices/translationDisplaySlice';
import { translationInfoSlice } from './slices/translationInfoSlice';

/**
 * localStorage書き込み失敗の累計回数(単調増加)。
 *
 * persistor.flush() は書き込みに失敗しても reject せず resolve するため、
 * 呼び出し元が「今回の flush() で書き込みが失敗したか」を知るには
 * flush() 前後でこの値を比較する必要がある({@link getWriteFailureCount} 参照)。
 * 時刻(Date.now())ではなくカウンターにしているのは、同じミリ秒内に複数回失敗した
 * 場合に値が一致し、失敗を見逃してしまうのを避けるため。
 *
 * PRISM-3110: この値自体はwhitelist内の全State(通訳履歴等)を含めた書き込み全体の
 * 失敗を検知するが、ユーザーへの通知は用語集登録の文脈でのみ行う
 * (`useGlossaryEntries`参照)。用語集以外が原因の書き込み失敗はスコープ外とし、
 * 通知は行わない(実際に容量超過が起こりうるのはほぼ用語集の大量登録のみのため)。
 */
let writeFailureCount = 0;

/**
 * 直近のlocalStorage書き込み失敗の原因となった例外の`error.name`。
 *
 * `localStorage.setItem`が投げうる代表的な例外は以下の2種類で、原因によって
 * ユーザーに示すべき案内が異なる({@link getLastWriteFailureErrorName} 参照)。
 * - `QuotaExceededError`: 保存容量の上限超過(件数を減らせば解決する可能性がある)
 * - それ以外(`SecurityError`等): プライベートブラウジングやブラウザ設定による
 *   保存不可(件数を減らしても解決しない)
 */
let lastWriteFailureErrorName: string | undefined;

/**
 * localStorage書き込み失敗の累計回数を返す。
 *
 * 例: 用語集登録時に、登録前後でこの値を比較することで
 * 「登録操作に起因する書き込みが失敗したか」を判定できる(PRISM-3110)。
 */
export const getWriteFailureCount = (): number => writeFailureCount;

/**
 * 直近のlocalStorage書き込み失敗の原因となった例外の`error.name`を返す。
 *
 * {@link getWriteFailureCount} で「今回の操作中に書き込みが失敗したか」を
 * 判定した後、このタイミングでの値を読むことで、その失敗の原因を判定できる
 * (単一の書き込み処理の中で複数の異なる失敗が同時に発生することは無いため)。
 */
export const getLastWriteFailureErrorName = (): string | undefined =>
  lastWriteFailureErrorName;

const persistConfig = {
  // storeに保存されるkey名
  key: 'root',
  // local storageに保存
  // (書き込み失敗を検知できるよう、標準のstorageではなく独自アダプターを使う)
  storage: localStorageAdapter,
  // storageに保存するリスト
  whitelist: [
    browserLanguageSlice.name,
    browserUILanguageSlice.name,
    browserAudioSettingSlice.name,
    browserHistorySlice.name,
    browserTranslationDisplaySlice.name,
    loginUserInfoSlice.name,
    glossaryEntriesSlice.name,
  ],
  // マイグレーション用
  version: 1,
  debug: true,
  /**
   * localStorageへの書き込みに失敗した場合(容量超過の QuotaExceededError など)の処理。
   *
   * redux-persist は書き込み失敗をデフォルトで握りつぶすため、失敗回数とエラー名だけ記録する。
   * ユーザーへの通知は行わない(用語集登録ダイアログ側で、書き込み結果を見て
   * 具体的な案内を表示する。PRISM-3110)。
   */
  writeFailHandler: (error: Error) => {
    // eslint-disable-next-line no-console
    console.error('redux-persistのstate書き込みに失敗しました', error);
    writeFailureCount += 1;
    lastWriteFailureErrorName = error.name;
  },
};

export const rootReducer = combineReducers({
  [browserLanguageSlice.name]: browserLanguageSlice.reducer,
  [translationInfoSlice.name]: translationInfoSlice.reducer,
  [audioInfoSlice.name]: audioInfoSlice.reducer,
  [translationDisplaySlice.name]: translationDisplaySlice.reducer,
  [toastInfoSlice.name]: toastInfoSlice.reducer,
  [browserUILanguageSlice.name]: browserUILanguageSlice.reducer,
  [browserHistorySlice.name]: browserHistorySlice.reducer,
  [browserTranslationDisplaySlice.name]: browserTranslationDisplaySlice.reducer,
  [googleTtsVoiceListSlice.name]: googleTtsVoiceListSlice.reducer,
  [shareViewInfoSlice.name]: shareViewInfoSlice.reducer,
  [shareViewEndInfoSlice.name]: shareViewEndInfoSlice.reducer,
  [commonErrorDialogSlice.name]: commonErrorDialogSlice.reducer,
  [guestDisplaySlice.name]: guestDisplaySlice.reducer,
  [languageListSlice.name]: languageListSlice.reducer,
  [browserAudioSettingSlice.name]: browserAudioSettingSlice.reducer,
  [loginUserInfoSlice.name]: loginUserInfoSlice.reducer,
  [authSerialSlice.name]: authSerialSlice.reducer,
  [glossaryEntriesSlice.name]: glossaryEntriesSlice.reducer,
});

// 永続化設定されたReducerとして定義
const persistedReducer = persistReducer(persistConfig, rootReducer);

// https://github.com/rt2zz/redux-persist/issues/988
// こちらを参考に「persist」キーの「serializable」のチェックを無視
const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ['persist/PERSIST'],
      },
    }),
});

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export const useAppDispatch = (): AppDispatch => useDispatch<AppDispatch>();

export default store;
